import { useEffect, useCallback } from "react";
import Win2kButton from "@/components/win2k/Win2kButton";
import { cn } from "@/lib/utils";

interface KickablePlayer {
  username: string;
  connected: boolean;
}

interface KickPlayerPickerProps {
  players: KickablePlayer[];
  open: boolean;
  onSelect: (username: string) => void;
  onCancel: () => void;
}

export default function KickPlayerPicker({
  players,
  open,
  onSelect,
  onCancel,
}: KickPlayerPickerProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    },
    [onCancel]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="win2k-outset bg-[var(--win2k-window-bg)] max-w-xs w-full mx-4">
        <div className="win2k-title-bar px-1 py-0.5 min-h-[22px]">
          <span className="text-xs font-bold">Kick Player</span>
        </div>
        <div className="p-3 space-y-3">
          <p className="text-[11px] text-[var(--win2k-button-text)]">
            Select a player to remove from the room:
          </p>
          <div className="win2k-inset p-1 max-h-48 overflow-y-auto">
            {players.length === 0 ? (
              <p className="text-[11px] text-[var(--win2k-shadow)] px-1 py-2 text-center">
                No players to kick
              </p>
            ) : (
              players.map((player) => (
                <button
                  key={player.username}
                  type="button"
                  onClick={() => onSelect(player.username)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-1 text-[12px] text-left cursor-default",
                    "hover:bg-[var(--win2k-title-blue-start)] hover:text-white"
                  )}
                >
                  <span
                    className={cn(
                      "w-2 h-2 rounded-full shrink-0",
                      player.connected ? "bg-green-600" : "bg-yellow-600"
                    )}
                    title={player.connected ? "Connected" : "Disconnected"}
                  />
                  <span className="flex-1 min-w-0 truncate">
                    {player.username}
                  </span>
                  {!player.connected && (
                    <span className="text-[10px] text-yellow-700 shrink-0">
                      disconnected
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
          <div className="flex justify-end">
            <Win2kButton onClick={onCancel} className="min-w-[75px]" autoFocus>
              Cancel
            </Win2kButton>
          </div>
        </div>
      </div>
    </div>
  );
}
